import React, { useEffect, useMemo, useState } from "react";
import { API, graphqlOperation } from "aws-amplify";
import { CalendarDays, FileSpreadsheet, Link as LinkIcon, MapPinned, RefreshCw, TriangleAlert } from "lucide-react";
import AppDialog from "../components/AppDialog";
import { formatApiError } from "../lib/formatApiError";
import { isProgramAdmin } from "../lib/roles";

interface SchedulePageProps {
  userRoles: string[];
}

interface Organization {
  id: string;
  name: string;
}

interface Field {
  id: string;
  organizationId: string;
  name: string;
  address?: string | null;
  town?: string | null;
  state?: string | null;
  surfaceType?: string | null;
  notes?: string | null;
}

interface ExternalScheduleSource {
  id: string;
  organizationId: string;
  name: string;
  sourceType: string;
  url?: string | null;
  status: string;
  lastImportedAt?: string | null;
  lastError?: string | null;
  notes?: string | null;
}

interface GameSchedule {
  id: string;
  organizationId: string;
  teamId?: string | null;
  leagueId?: string | null;
  fieldId?: string | null;
  title: string;
  scheduleType?: string | null;
  date: string;
  startTime?: string | null;
  endTime?: string | null;
  opponentName?: string | null;
  externalSourceId?: string | null;
  externalEventId?: string | null;
  conflictStatus?: string | null;
  conflictDetails?: string | null;
  notes?: string | null;
}

interface ParsedScheduleEvent {
  uid: string;
  title: string;
  date: string;
  startTime: string;
  endTime: string;
  fieldName: string;
  scheduleType: string;
  notes?: string;
}

const listOrganizationsQuery = /* GraphQL */ `
  query ListOrganizationsForSchedule($limit: Int) {
    listOrganizations(limit: $limit) { items { id name } }
  }
`;

const listFieldsQuery = /* GraphQL */ `
  query ListFieldsForSchedule($limit: Int) {
    listFields(limit: $limit) {
      items { id organizationId name address town state surfaceType notes }
    }
  }
`;

const listSourcesQuery = /* GraphQL */ `
  query ListExternalScheduleSourcesForSchedule($limit: Int) {
    listExternalScheduleSources(limit: $limit) {
      items { id organizationId name sourceType url status lastImportedAt lastError notes }
    }
  }
`;

const listGameSchedulesQuery = /* GraphQL */ `
  query ListGameSchedulesForSchedule($limit: Int) {
    listGameSchedules(limit: $limit) {
      items {
        id organizationId teamId leagueId fieldId title scheduleType date startTime endTime opponentName
        externalSourceId externalEventId conflictStatus conflictDetails notes
      }
    }
  }
`;

const createFieldMutation = /* GraphQL */ `
  mutation CreateFieldForSchedule($input: CreateFieldInput!) {
    createField(input: $input) { id organizationId name address town state surfaceType notes }
  }
`;

const createSourceMutation = /* GraphQL */ `
  mutation CreateExternalScheduleSourceForSchedule($input: CreateExternalScheduleSourceInput!) {
    createExternalScheduleSource(input: $input) {
      id organizationId name sourceType url status lastImportedAt lastError notes
    }
  }
`;

const updateSourceMutation = /* GraphQL */ `
  mutation UpdateExternalScheduleSourceForSchedule($input: UpdateExternalScheduleSourceInput!) {
    updateExternalScheduleSource(input: $input) {
      id organizationId name sourceType url status lastImportedAt lastError notes
    }
  }
`;

const createGameScheduleMutation = /* GraphQL */ `
  mutation CreateGameScheduleForSchedule($input: CreateGameScheduleInput!) {
    createGameSchedule(input: $input) {
      id organizationId fieldId title scheduleType date startTime endTime externalSourceId externalEventId conflictStatus conflictDetails notes
    }
  }
`;

const builtinSources = [
  {
    name: "HHK/SR Field Calendar",
    sourceType: "GOOGLE_ICAL",
    url: "https://calendar.google.com/calendar/ical/hhksrfields%40gmail.com/public/basic.ics",
    notes: "The HHK/SR page embeds this public Google Calendar feed."
  },
  {
    name: "USR Baseball 10U LeagueLineup",
    sourceType: "LEAGUELINEUP_ICAL",
    url: "https://www.leaguelineup.com/schedulelink.asp?s=232230&d=730047&t=99999",
    notes: "LeagueLineup exposes one iCal URL per selected division/team filter."
  },
  {
    name: "Allendale EZFacility",
    sourceType: "EZFACILITY_HTML",
    url: "https://login.ezfacility.com/app/login.aspx?vouname=allendalevo&vopassword=Viewonly1",
    notes: "Requires server-side scraping of authenticated ASP.NET HTML. Direct browser fetch will usually fail due CORS/session behavior."
  }
];

const decodeIcsValue = (value: string) =>
  value
    .replace(/\\n/g, "\n")
    .replace(/\\,/g, ",")
    .replace(/\\;/g, ";")
    .replace(/=0D=0A/g, "\n")
    .replace(/=([0-9A-F]{2})/gi, (_, hex) => String.fromCharCode(Number.parseInt(hex, 16)));

const unfoldIcs = (text: string) => text.replace(/\r?\n[ \t]/g, "");

const getIcsField = (block: string, fieldName: string) => {
  const line = block.split(/\r?\n/).find((item) => item.toUpperCase().startsWith(fieldName));
  if (!line) {
    return "";
  }
  return decodeIcsValue(line.slice(line.indexOf(":") + 1).trim());
};

const parseIcsDate = (value: string) => {
  if (!value) {
    return { date: "", time: "" };
  }
  const normalized = value.trim();
  if (/^\d{8}$/.test(normalized)) {
    return {
      date: `${normalized.slice(0, 4)}-${normalized.slice(4, 6)}-${normalized.slice(6, 8)}`,
      time: "00:00:00"
    };
  }
  const match = normalized.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})?Z?$/);
  if (!match) {
    return { date: "", time: "" };
  }
  const iso = `${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}:${match[6] ?? "00"}${normalized.endsWith("Z") ? "Z" : ""}`;
  const parsed = new Date(iso);
  if (!Number.isNaN(parsed.getTime()) && normalized.endsWith("Z")) {
    return {
      date: parsed.toLocaleDateString("en-CA"),
      time: parsed.toTimeString().slice(0, 8)
    };
  }
  return { date: `${match[1]}-${match[2]}-${match[3]}`, time: `${match[4]}:${match[5]}:${match[6] ?? "00"}` };
};

const inferScheduleType = (title: string, notes = "") => {
  const text = `${title} ${notes}`.toLowerCase();
  if (text.includes("practice")) {
    return "PRACTICE";
  }
  if (text.includes("game") || text.includes("@") || text.includes(" vs ")) {
    return "GAME";
  }
  return "EVENT";
};

const normalizeScheduleType = (value: string, fallbackTitle: string, fallbackNotes = "") => {
  const type = value.match(/\b(PRACTICE|GAME|EVENT)\b/i)?.[1];
  return (type || inferScheduleType(fallbackTitle, fallbackNotes)).toUpperCase();
};

const todayIso = () => {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  });
  return formatter.format(new Date());
};

const isFutureOrToday = (date: string) => date >= todayIso();

const parseIcsEvents = (text: string): ParsedScheduleEvent[] => {
  const unfolded = unfoldIcs(text);
  const blocks = unfolded.match(/BEGIN:VEVENT[\s\S]*?END:VEVENT/g) ?? [];
  return blocks
    .map((block, index) => {
      const uid = getIcsField(block, "UID") || `ics-${index}`;
      const title = getIcsField(block, "SUMMARY") || "Untitled event";
      const location = getIcsField(block, "LOCATION") || "Unassigned field";
      const description = getIcsField(block, "DESCRIPTION");
      const startRaw = getIcsField(block, "DTSTART");
      const endRaw = getIcsField(block, "DTEND");
      const start = parseIcsDate(startRaw);
      const end = parseIcsDate(endRaw);
      return {
        uid,
        title,
        fieldName: location,
        date: start.date,
        startTime: start.time,
        endTime: end.time || start.time,
        scheduleType: inferScheduleType(title, description),
        notes: description
      };
    })
    .filter((event) => event.date && event.startTime && isFutureOrToday(event.date));
};

const splitCsvLine = (line: string): string[] => {
  const out: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (char === "," && !inQuotes) {
      out.push(current.trim());
      current = "";
      continue;
    }
    current += char;
  }
  out.push(current.trim());
  return out;
};

const normalizeHeader = (value: string) => value.trim().toLowerCase().replace(/[\s_-]+/g, "");

const normalizeDate = (value: string) => {
  const trimmed = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }
  const match = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (match) {
    return `${match[3]}-${match[1].padStart(2, "0")}-${match[2].padStart(2, "0")}`;
  }
  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toISOString().slice(0, 10);
};

const normalizeTime = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) {
    return "";
  }
  if (/^\d{2}:\d{2}(:\d{2})?$/.test(trimmed)) {
    return trimmed.length === 5 ? `${trimmed}:00` : trimmed;
  }
  const parsed = new Date(`2000-01-01 ${trimmed}`);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toTimeString().slice(0, 8);
};

const parseCsvEvents = (text: string): ParsedScheduleEvent[] => {
  const lines = text.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) {
    return [];
  }
  const headers = splitCsvLine(lines[0]).map(normalizeHeader);
  const indexOf = (...names: string[]) => names.map(normalizeHeader).map((name) => headers.indexOf(name)).find((index) => index >= 0) ?? -1;
  const dateIndex = indexOf("date", "game date", "start date");
  const startIndex = indexOf("start", "start time", "time");
  const endIndex = indexOf("end", "end time");
  const fieldIndex = indexOf("field", "location", "venue");
  const titleIndex = indexOf("title", "event", "team", "home", "summary");
  const typeIndex = indexOf("type", "practice/game", "schedule type");
  return lines.slice(1).map((line, index) => {
    const cells = splitCsvLine(line);
    const title = cells[titleIndex] || "Imported event";
    const notes = cells.join(" | ");
    return {
      uid: `csv-${index}-${cells.join("-")}`,
      title,
      date: normalizeDate(cells[dateIndex] || ""),
      startTime: normalizeTime(cells[startIndex] || ""),
      endTime: normalizeTime(cells[endIndex] || ""),
      fieldName: cells[fieldIndex] || "Unassigned field",
      scheduleType: normalizeScheduleType(cells[typeIndex] || "", title, notes),
      notes
    };
  }).filter((event) => event.date && event.startTime && isFutureOrToday(event.date));
};

const decodeHtml = (value: string) => {
  const named: Record<string, string> = {
    amp: "&",
    quot: '"',
    apos: "'",
    nbsp: " ",
    lt: "<",
    gt: ">"
  };
  return value
    .replace(/&#x([0-9a-f]+);/gi, (_, hex) => String.fromCharCode(Number.parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number.parseInt(code, 10)))
    .replace(/&([a-z]+);/gi, (match, key) => named[key.toLowerCase()] ?? match);
};

const stripTags = (value: string) => decodeHtml(value.replace(/<[^>]+>/g, " ")).replace(/\s+/g, " ").trim();

const getEzFacilityPageDate = (text: string) =>
  normalizeDate(getEzAttr(
    text.match(/<input[^>]*(?:id="jqStartDate_txtTextBox"|name="jqStartDate\$txtTextBox")[^>]*>/i)?.[0] ?? "",
    "value"
  ));

const getEzFacilityResources = (text: string) =>
  Array.from(text.matchAll(/<td[^>]*class="overviewResourceName"[^>]*>([\s\S]*?)<\/td>/gi))
    .map((match) => stripTags(match[1]))
    .filter(Boolean);

const getEzAttr = (tag: string, attr: string) =>
  tag.match(new RegExp(`${attr}="([\\s\\S]*?)"`, "i"))?.[1] ?? "";

const parseEzFacilityNoteRows = (
  decodedTag: string,
  fallbackTitle: string,
  fallbackType: string,
  eventId: string,
  rowFieldName: string
): ParsedScheduleEvent[] => {
  const notesText = decodedTag
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/div>/gi, "\n")
    .replace(/\\t/g, "\t")
    .replace(/<[^>]+>/g, " ");
  const events: ParsedScheduleEvent[] = [];
  notesText
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((line, index) => {
      const cells = line.split("\t").map((cell) => cell.trim());
      if (cells.length < 8 || !/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(cells[0])) {
        return;
      }
      const date = normalizeDate(cells[0]);
      const team = cells[3] || fallbackTitle;
      const scheduleType = normalizeScheduleType(cells[7] || fallbackType, team, decodedTag);
      const notes = stripTags(decodedTag);
      const parsed = {
        uid: `ezfacility-${eventId || "event"}-${date}-${index}-${team}-${cells[6] || rowFieldName}`,
        title: team,
        date,
        startTime: normalizeTime(cells[4]),
        endTime: normalizeTime(cells[5]),
        fieldName: cells[6] || rowFieldName || "EZFacility field",
        scheduleType,
        ...(notes ? { notes } : {})
      };
      if (parsed.date && parsed.startTime && isFutureOrToday(parsed.date)) {
        events.push(parsed);
      }
    });
  return events;
};

const parseEzFacilityHtmlEvents = (text: string): ParsedScheduleEvent[] => {
  const pageDate = getEzFacilityPageDate(text);
  const resources = getEzFacilityResources(text);
  const rows = text.match(/<tr[^>]*class="o_cal_row"[\s\S]*?<\/tr>/gi) ?? [];
  const parsed: ParsedScheduleEvent[] = [];

  rows.forEach((row, rowIndex) => {
    const rowFieldName = resources[Math.floor(rowIndex / 2)] || "EZFacility field";
    const eventTags = row.match(/<td\b(?=[^>]*\bo_res\b)[\s\S]*?<\/td>/gi) ?? [];
    eventTags.forEach((tag, eventIndex) => {
      const decodedTag = decodeHtml(tag).replace(/\\'/g, "'").replace(/\\"/g, '"');
      const mouseover = decodeHtml(getEzAttr(tag, "onmouseover")).replace(/\\'/g, "'").replace(/\\"/g, '"');
      const eventId = mouseover.match(/\(#([^)]+)\)/)?.[1]?.trim() ?? "";
      const captionTime = mouseover.match(/\(#?[^)]*\)\s*([0-9]{1,2}:[0-9]{2}\s*(?:AM|PM)?)\s*-\s*([0-9]{1,2}:[0-9]{2}\s*(?:AM|PM)?)/i);
      const rawTitle = decodedTag.match(/overdivtitle>\s*Title:\s*([\s\S]*?)<\/div>/i)?.[1]
        || decodedTag.match(/class=['"]ov_data['"][^>]*>([\s\S]*?)<\/span>/i)?.[1]
        || "EZFacility event";
      const title = stripTags(rawTitle).replace(/:\s*#\d+\s*$/, "").trim() || "EZFacility event";
      const scheduleType = normalizeScheduleType(decodedTag.match(/overdivRT>\s*Type:\s*([\s\S]*?)<\/div>/i)?.[1] ?? "", title, decodedTag);
      const expandedRows = parseEzFacilityNoteRows(decodedTag, title, scheduleType, eventId, rowFieldName);
      if (expandedRows.length > 0) {
        parsed.push(...expandedRows);
        return;
      }
      if (!pageDate || !captionTime) {
        return;
      }
      parsed.push({
        uid: `ezfacility-${eventId || `${rowIndex}-${eventIndex}`}-${pageDate}`,
        title,
        date: pageDate,
        startTime: normalizeTime(captionTime[1]),
        endTime: normalizeTime(captionTime[2]),
        fieldName: rowFieldName,
        scheduleType,
        ...(stripTags(decodedTag) ? { notes: stripTags(decodedTag) } : {})
      });
    });
  });

  const unique = new Map<string, ParsedScheduleEvent>();
  parsed
    .filter((event) => event.date && event.startTime && isFutureOrToday(event.date))
    .forEach((event) => unique.set(event.uid, event));
  return Array.from(unique.values());
};

const parseScheduleText = (text: string): ParsedScheduleEvent[] => {
  if (text.includes("BEGIN:VCALENDAR")) {
    return parseIcsEvents(text);
  }
  if (/<html|overlib\(|overviewResourceName|o_cal_row/i.test(text)) {
    const ezEvents = parseEzFacilityHtmlEvents(text);
    if (ezEvents.length > 0) {
      return ezEvents;
    }
  }
  return parseCsvEvents(text);
};

const timeToMinutes = (value?: string | null) => {
  if (!value) {
    return null;
  }
  const [hours, minutes] = value.split(":").map((item) => Number.parseInt(item, 10));
  return Number.isFinite(hours) && Number.isFinite(minutes) ? hours * 60 + minutes : null;
};

const eventsOverlap = (left: Pick<GameSchedule, "date" | "startTime" | "endTime" | "fieldId">, right: Pick<GameSchedule, "date" | "startTime" | "endTime" | "fieldId">) => {
  if (!left.fieldId || !right.fieldId || left.fieldId !== right.fieldId || left.date !== right.date) {
    return false;
  }
  const leftStart = timeToMinutes(left.startTime);
  const rightStart = timeToMinutes(right.startTime);
  const leftEnd = timeToMinutes(left.endTime) ?? (leftStart === null ? null : leftStart + 120);
  const rightEnd = timeToMinutes(right.endTime) ?? (rightStart === null ? null : rightStart + 120);
  if (leftStart === null || rightStart === null || leftEnd === null || rightEnd === null) {
    return false;
  }
  return leftStart < rightEnd && rightStart < leftEnd;
};

const SchedulePage: React.FC<SchedulePageProps> = ({ userRoles }) => {
  const canManage = isProgramAdmin(userRoles);
  const [view, setView] = useState<"week" | "fields" | "imports">("week");
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [fields, setFields] = useState<Field[]>([]);
  const [sources, setSources] = useState<ExternalScheduleSource[]>([]);
  const [events, setEvents] = useState<GameSchedule[]>([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [fieldOpen, setFieldOpen] = useState(false);
  const [sourceOpen, setSourceOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importSourceId, setImportSourceId] = useState("");
  const [importText, setImportText] = useState("");
  const [newField, setNewField] = useState({ name: "", town: "", state: "NJ", address: "", surfaceType: "", notes: "" });
  const [newSource, setNewSource] = useState({ name: "", sourceType: "ICAL", url: "", notes: "" });

  const loadData = async (silent = false) => {
    try {
      if (!silent) {
        setErrorMessage("");
      }
      const [orgRes, fieldRes, sourceRes, eventRes] = await Promise.all([
        API.graphql(graphqlOperation(listOrganizationsQuery, { limit: 500 })),
        API.graphql(graphqlOperation(listFieldsQuery, { limit: 1000 })),
        API.graphql(graphqlOperation(listSourcesQuery, { limit: 1000 })),
        API.graphql(graphqlOperation(listGameSchedulesQuery, { limit: 3000 }))
      ]);
      const orgItems = ((orgRes as any)?.data?.listOrganizations?.items ?? []).filter(Boolean);
      setOrganizations(orgItems);
      setFields(((fieldRes as any)?.data?.listFields?.items ?? []).filter(Boolean));
      setSources(((sourceRes as any)?.data?.listExternalScheduleSources?.items ?? []).filter(Boolean));
      setEvents(((eventRes as any)?.data?.listGameSchedules?.items ?? []).filter(Boolean));
      if (!selectedOrganizationId && orgItems[0]?.id) {
        setSelectedOrganizationId(orgItems[0].id);
      }
    } catch (error) {
      if (!silent) {
        setErrorMessage(`Failed to load schedule data: ${formatApiError(error)}`);
      }
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const orgFields = useMemo(
    () => fields.filter((field) => !selectedOrganizationId || field.organizationId === selectedOrganizationId),
    [fields, selectedOrganizationId]
  );
  const orgSources = useMemo(
    () => sources.filter((source) => !selectedOrganizationId || source.organizationId === selectedOrganizationId),
    [selectedOrganizationId, sources]
  );
  const fieldById = useMemo(
    () => fields.reduce<Record<string, Field>>((acc, field) => ({ ...acc, [field.id]: field }), {}),
    [fields]
  );
  const sourceById = useMemo(
    () => sources.reduce<Record<string, ExternalScheduleSource>>((acc, source) => ({ ...acc, [source.id]: source }), {}),
    [sources]
  );
  const orgEvents = useMemo(
    () =>
      events
        .filter((event) => !selectedOrganizationId || event.organizationId === selectedOrganizationId)
        .sort((a, b) => `${a.date} ${a.startTime ?? ""}`.localeCompare(`${b.date} ${b.startTime ?? ""}`)),
    [events, selectedOrganizationId]
  );
  const conflicts = useMemo(
    () =>
      orgEvents.filter((event, index) =>
        orgEvents.some((candidate, candidateIndex) => candidateIndex !== index && eventsOverlap(event, candidate))
      ),
    [orgEvents]
  );

  const createField = async () => {
    if (!selectedOrganizationId || !newField.name.trim()) {
      setErrorMessage("Organization and field name are required.");
      return;
    }
    try {
      setErrorMessage("");
      await API.graphql(graphqlOperation(createFieldMutation, {
        input: {
          organizationId: selectedOrganizationId,
          name: newField.name.trim(),
          town: newField.town.trim() || null,
          state: newField.state.trim() || null,
          address: newField.address.trim() || null,
          surfaceType: newField.surfaceType.trim() || null,
          notes: newField.notes.trim() || null
        }
      }));
      setNewField({ name: "", town: "", state: "NJ", address: "", surfaceType: "", notes: "" });
      setFieldOpen(false);
      await loadData(true);
    } catch (error) {
      setErrorMessage(`Failed to create field: ${formatApiError(error)}`);
    }
  };

  const createSource = async () => {
    if (!selectedOrganizationId || !newSource.name.trim()) {
      setErrorMessage("Organization and source name are required.");
      return;
    }
    try {
      setErrorMessage("");
      await API.graphql(graphqlOperation(createSourceMutation, {
        input: {
          organizationId: selectedOrganizationId,
          name: newSource.name.trim(),
          sourceType: newSource.sourceType,
          url: newSource.url.trim() || null,
          status: "ACTIVE",
          notes: newSource.notes.trim() || null
        }
      }));
      setNewSource({ name: "", sourceType: "ICAL", url: "", notes: "" });
      setSourceOpen(false);
      await loadData(true);
    } catch (error) {
      setErrorMessage(`Failed to create source: ${formatApiError(error)}`);
    }
  };

  const findOrCreateField = async (fieldName: string) => {
    const normalized = fieldName.trim().toLowerCase();
    const existing = orgFields.find((field) => field.name.trim().toLowerCase() === normalized);
    if (existing) {
      return existing.id;
    }
    const created: any = await API.graphql(graphqlOperation(createFieldMutation, {
      input: {
        organizationId: selectedOrganizationId,
        name: fieldName.trim() || "Unassigned field",
        state: "NJ",
        notes: "Created automatically during schedule import."
      }
    }));
    return created?.data?.createField?.id as string;
  };

  const importEvents = async () => {
    if (!selectedOrganizationId) {
      setErrorMessage("Select an organization first.");
      return;
    }
    const selectedSource = importSourceId ? sourceById[importSourceId] : null;
    let raw = importText.trim();
    try {
      setErrorMessage("");
      setStatusMessage("");
      if (!raw && selectedSource?.url) {
        try {
          const response = await fetch(selectedSource.url);
          raw = await response.text();
        } catch {
          throw new Error("The browser could not fetch that URL directly. Paste the iCal/CSV contents, or use the server-side importer Lambda for this source.");
        }
      }
      const rawParsed = parseScheduleText(raw);
      const parsed = rawParsed.filter((event) => isFutureOrToday(event.date));
      if (parsed.length === 0) {
        throw new Error("No future schedule events were detected. Use iCal text or CSV with date/start/field/title columns.");
      }
      const existingByExternalId = new Set(events.map((event) => event.externalEventId).filter(Boolean));
      let createdCount = 0;
      const stagedExisting = [...events];
      for (const parsedEvent of parsed) {
        if (existingByExternalId.has(parsedEvent.uid)) {
          continue;
        }
        const fieldId = await findOrCreateField(parsedEvent.fieldName);
        const draft: GameSchedule = {
          id: `draft-${parsedEvent.uid}`,
          organizationId: selectedOrganizationId,
          fieldId,
          title: parsedEvent.title,
          scheduleType: parsedEvent.scheduleType,
          date: parsedEvent.date,
          startTime: parsedEvent.startTime,
          endTime: parsedEvent.endTime,
          externalSourceId: selectedSource?.id ?? null,
          externalEventId: parsedEvent.uid,
          notes: parsedEvent.notes ?? null
        };
        const hasConflict = stagedExisting.some((event) => eventsOverlap(draft, event));
        await API.graphql(graphqlOperation(createGameScheduleMutation, {
          input: {
            organizationId: selectedOrganizationId,
            fieldId,
            title: parsedEvent.title,
            scheduleType: parsedEvent.scheduleType,
            date: parsedEvent.date,
            startTime: parsedEvent.startTime,
            endTime: parsedEvent.endTime || null,
            externalSourceId: selectedSource?.id ?? null,
            externalEventId: parsedEvent.uid,
            conflictStatus: hasConflict ? "CONFLICT" : "CLEAR",
            conflictDetails: hasConflict ? "Overlaps an existing event on the same field." : null,
            notes: parsedEvent.notes ?? null
          }
        }));
        stagedExisting.push(draft);
        createdCount += 1;
      }
      if (selectedSource) {
        await API.graphql(graphqlOperation(updateSourceMutation, {
          input: {
            id: selectedSource.id,
            lastImportedAt: new Date().toISOString(),
            lastError: null
          }
        }));
      }
      setImportOpen(false);
      setImportText("");
      setImportSourceId("");
      setStatusMessage(`Imported ${createdCount} new schedule event${createdCount === 1 ? "" : "s"}.`);
      await loadData(true);
    } catch (error) {
      const message = formatApiError(error);
      setErrorMessage(`Import failed: ${message}`);
      if (selectedSource) {
        try {
          await API.graphql(graphqlOperation(updateSourceMutation, { input: { id: selectedSource.id, lastError: message } }));
        } catch {}
      }
    }
  };

  const seedBuiltinSource = (source: typeof builtinSources[number]) => {
    setNewSource({
      name: source.name,
      sourceType: source.sourceType,
      url: source.url,
      notes: source.notes
    });
    setSourceOpen(true);
  };

  return (
    <div className="platform-page">
      <section className="platform-hero">
        <div>
          <div className="platform-eyebrow">Schedule / Field Availability</div>
          <h1>Import town calendars, catch conflicts, and keep fields sane.</h1>
          <p>
            HHK/SR has a Google Calendar ICS feed, LeagueLineup has per-filter iCal feeds, and EZFacility HTML can be pasted or handled server-side.
            This page now stores fields, external sources, imported events, and conflict status.
          </p>
        </div>
        <div className="platform-hero__actions">
          <button className="bg-slate-200 text-slate-900 px-4 py-2 rounded" onClick={() => loadData()}>
            <RefreshCw size={17} /> Refresh
          </button>
          {canManage && (
            <>
              <button className="bg-blue-600 text-white px-4 py-2 rounded" onClick={() => setImportOpen(true)}>
                <FileSpreadsheet size={17} /> Import
              </button>
              <button className="bg-green-600 text-white px-4 py-2 rounded" onClick={() => setSourceOpen(true)}>
                <LinkIcon size={17} /> Add Source
              </button>
            </>
          )}
        </div>
      </section>

      {errorMessage && <p style={{ color: "#c1121f" }}>{errorMessage}</p>}
      {statusMessage && <p style={{ color: "#166534" }}>{statusMessage}</p>}

      <div className="bubble-card p-4 grid grid-cols-1 md:grid-cols-4 gap-2">
        <select value={selectedOrganizationId} onChange={(event) => setSelectedOrganizationId(event.target.value)} className="border px-3 py-2 rounded">
          <option value="">Select organization</option>
          {organizations.map((organization) => (
            <option key={organization.id} value={organization.id}>{organization.name}</option>
          ))}
        </select>
        <div className="bubble-pill">Fields: {orgFields.length}</div>
        <div className="bubble-pill">Events: {orgEvents.length}</div>
        <div className={conflicts.length ? "bubble-pill bubble-pill--danger" : "bubble-pill"}>Conflicts: {conflicts.length}</div>
      </div>

      <div className="platform-tabs" role="tablist" aria-label="Schedule sections">
        <button className={view === "week" ? "platform-tab platform-tab--active" : "platform-tab"} onClick={() => setView("week")}>
          <CalendarDays size={16} /> Events
        </button>
        <button className={view === "fields" ? "platform-tab platform-tab--active" : "platform-tab"} onClick={() => setView("fields")}>
          <MapPinned size={16} /> Fields
        </button>
        <button className={view === "imports" ? "platform-tab platform-tab--active" : "platform-tab"} onClick={() => setView("imports")}>
          <FileSpreadsheet size={16} /> Sources
        </button>
      </div>

      {view === "week" && (
        <div className="bubble-table-wrapper">
          <table className="bubble-table platform-table--roomy">
            <thead>
              <tr>
                <th>Date</th>
                <th>Time</th>
                <th>Title</th>
                <th>Type</th>
                <th>Field</th>
                <th>Source</th>
                <th>Conflict</th>
              </tr>
            </thead>
            <tbody>
              {orgEvents.length === 0 && (
                <tr><td colSpan={7} className="text-gray-600">No imported schedule events yet.</td></tr>
              )}
              {orgEvents.map((event) => (
                <tr key={event.id}>
                  <td>{event.date}</td>
                  <td>{event.startTime?.slice(0, 5) ?? "-"}{event.endTime ? ` - ${event.endTime.slice(0, 5)}` : ""}</td>
                  <td>{event.title}</td>
                  <td><span className="bubble-pill">{event.scheduleType ?? "EVENT"}</span></td>
                  <td>{event.fieldId ? fieldById[event.fieldId]?.name ?? "Unknown field" : "Unassigned"}</td>
                  <td>{event.externalSourceId ? sourceById[event.externalSourceId]?.name ?? "Imported" : "Manual"}</td>
                  <td>
                    {event.conflictStatus === "CONFLICT" || conflicts.some((conflict) => conflict.id === event.id) ? (
                      <span className="bubble-pill bubble-pill--danger">Conflict</span>
                    ) : (
                      <span className="bubble-pill">Clear</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {view === "fields" && (
        <>
          {canManage && (
            <div className="bubble-card p-4">
              <button className="bg-blue-600 text-white px-4 py-2 rounded" onClick={() => setFieldOpen(true)}>Add Field</button>
            </div>
          )}
          <div className="bubble-table-wrapper">
            <table className="bubble-table platform-table--roomy">
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Town</th>
                  <th>State</th>
                  <th>Surface</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {orgFields.length === 0 && <tr><td colSpan={5} className="text-gray-600">No fields yet.</td></tr>}
                {orgFields.map((field) => (
                  <tr key={field.id}>
                    <td>{field.name}</td>
                    <td>{field.town ?? "-"}</td>
                    <td>{field.state ?? "-"}</td>
                    <td>{field.surfaceType ?? "-"}</td>
                    <td>{field.notes ?? "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {view === "imports" && (
        <div className="platform-grid">
          <div className="bubble-card p-4 platform-panel">
            <h2>Saved Sources</h2>
            <div className="platform-step-list">
              {orgSources.length === 0 && <p className="text-gray-600">No sources saved for this organization yet.</p>}
              {orgSources.map((source) => (
                <div key={source.id} className="platform-source-card">
                  <LinkIcon size={20} />
                  <div>
                    <strong>{source.name}</strong>
                    <span>{source.sourceType} | {source.status} | {source.lastImportedAt ? `Last import ${source.lastImportedAt}` : "Never imported"}</span>
                    {source.lastError && <span style={{ color: "#c1121f" }}>{source.lastError}</span>}
                    {source.url && <span>{source.url}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="bubble-card p-4 platform-panel">
            <h2>Detected Source Recipes</h2>
            {builtinSources.map((source) => (
              <div key={source.name} className="platform-source-card">
                <FileSpreadsheet size={20} />
                <div>
                  <strong>{source.name}</strong>
                  <span>{source.notes}</span>
                  {canManage && (
                    <button className="bg-blue-600 text-white px-3 py-1 rounded mt-2" onClick={() => seedBuiltinSource(source)}>
                      Save Source
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <AppDialog
        open={fieldOpen}
        onClose={() => setFieldOpen(false)}
        title="Add Field"
        footer={
          <>
            <button className="bg-gray-200 text-gray-800 px-4 py-2 rounded" onClick={() => setFieldOpen(false)}>Cancel</button>
            <button className="bg-blue-600 text-white px-4 py-2 rounded" onClick={createField}>Save</button>
          </>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <input className="border px-3 py-2 rounded" value={newField.name} onChange={(event) => setNewField((prev) => ({ ...prev, name: event.target.value }))} placeholder="Field name" />
          <input className="border px-3 py-2 rounded" value={newField.surfaceType} onChange={(event) => setNewField((prev) => ({ ...prev, surfaceType: event.target.value }))} placeholder="Surface/type" />
          <input className="border px-3 py-2 rounded" value={newField.town} onChange={(event) => setNewField((prev) => ({ ...prev, town: event.target.value }))} placeholder="Town" />
          <input className="border px-3 py-2 rounded" value={newField.state} onChange={(event) => setNewField((prev) => ({ ...prev, state: event.target.value }))} placeholder="State" />
          <input className="border px-3 py-2 rounded md:col-span-2" value={newField.address} onChange={(event) => setNewField((prev) => ({ ...prev, address: event.target.value }))} placeholder="Address" />
          <textarea className="border px-3 py-2 rounded md:col-span-2" value={newField.notes} onChange={(event) => setNewField((prev) => ({ ...prev, notes: event.target.value }))} placeholder="Notes" />
        </div>
      </AppDialog>

      <AppDialog
        open={sourceOpen}
        onClose={() => setSourceOpen(false)}
        title="Add External Schedule Source"
        footer={
          <>
            <button className="bg-gray-200 text-gray-800 px-4 py-2 rounded" onClick={() => setSourceOpen(false)}>Cancel</button>
            <button className="bg-blue-600 text-white px-4 py-2 rounded" onClick={createSource}>Save</button>
          </>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <input className="border px-3 py-2 rounded" value={newSource.name} onChange={(event) => setNewSource((prev) => ({ ...prev, name: event.target.value }))} placeholder="Source name" />
          <select className="border px-3 py-2 rounded" value={newSource.sourceType} onChange={(event) => setNewSource((prev) => ({ ...prev, sourceType: event.target.value }))}>
            <option value="ICAL">iCal</option>
            <option value="GOOGLE_ICAL">Google Calendar iCal</option>
            <option value="LEAGUELINEUP_ICAL">LeagueLineup iCal</option>
            <option value="EZFACILITY_HTML">EZFacility HTML</option>
            <option value="CSV">CSV</option>
          </select>
          <input className="border px-3 py-2 rounded md:col-span-2" value={newSource.url} onChange={(event) => setNewSource((prev) => ({ ...prev, url: event.target.value }))} placeholder="URL" />
          <textarea className="border px-3 py-2 rounded md:col-span-2" value={newSource.notes} onChange={(event) => setNewSource((prev) => ({ ...prev, notes: event.target.value }))} placeholder="Notes" />
        </div>
      </AppDialog>

      <AppDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Import Schedule Events"
        wide
        footer={
          <>
            <button className="bg-gray-200 text-gray-800 px-4 py-2 rounded" onClick={() => setImportOpen(false)}>Cancel</button>
            <button className="bg-blue-600 text-white px-4 py-2 rounded" onClick={importEvents}>Import</button>
          </>
        }
      >
        <p className="text-sm text-gray-600 mb-3">
          Paste iCal, CSV, or EZFacility overview HTML. If the URL supports browser CORS, selecting a source with an empty text box will try to fetch it directly.
        </p>
        <select className="border px-3 py-2 rounded w-full mb-3" value={importSourceId} onChange={(event) => setImportSourceId(event.target.value)}>
          <option value="">No saved source / pasted import</option>
          {orgSources.map((source) => (
            <option key={source.id} value={source.id}>{source.name}</option>
          ))}
        </select>
        <textarea
          className="border px-3 py-2 rounded w-full"
          style={{ minHeight: 260, fontFamily: "monospace" }}
          value={importText}
          onChange={(event) => setImportText(event.target.value)}
          placeholder="Paste BEGIN:VCALENDAR... or CSV with Date, Start, End, Field, Title..."
        />
        <div className="platform-alert-row platform-alert-row--muted">
          <TriangleAlert size={18} />
          <div>
            <strong>EZFacility note</strong>
            <span>EZFacility browser URL fetches may still be blocked by CORS/session rules. If that happens, paste the overview HTML here; the importer will parse current/future events from it.</span>
          </div>
        </div>
      </AppDialog>
    </div>
  );
};

export default SchedulePage;
